import { useEffect, useRef, useState, useCallback } from 'react';
import { GameMessage, AnswerMessage, ReconnectTokenData } from '../types/game';
import { environment } from '../config/environment';

interface UseWebSocketOptions {
  gameId: string;
  playerName: string;
  onMessage: (message: GameMessage) => void;
  onError?: (error: string) => void;
}

const RECONNECT_TOKEN_KEY = 'quizcrunch_reconnect_token';
const RECONNECT_TOKEN_GAME_KEY = 'quizcrunch_reconnect_game';

export const useWebSocket = ({ gameId, playerName, onMessage, onError }: UseWebSocketOptions) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string>('');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intentionalDisconnectRef = useRef(false);
  const reconnectTokenRef = useRef<string | null>(null);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    if (!gameId || !playerName) return;

    // Reset intentional disconnect flag when connecting
    intentionalDisconnectRef.current = false;

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close existing connection if any
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      console.log('Closing existing connection before reconnecting');
      wsRef.current.close();
    }

    // Try to get reconnect token from localStorage (for this specific game)
    const storedToken = localStorage.getItem(RECONNECT_TOKEN_KEY);
    const storedGame = localStorage.getItem(RECONNECT_TOKEN_GAME_KEY);

    // Use stored token if it's for the same game and we haven't already stored it in ref
    if (storedToken && storedGame === gameId && !reconnectTokenRef.current) {
      reconnectTokenRef.current = storedToken;
      console.log('Found stored reconnect token for reconnection');
    }

    // Build WebSocket URL with reconnect token if available
    let wsUrl = `${environment.websocket.proxy}/game/${encodeURIComponent(gameId)}?role=player`;
    if (reconnectTokenRef.current) {
      wsUrl += `&reconnect_token=${encodeURIComponent(reconnectTokenRef.current)}`;
      console.log('Attempting reconnection with token');
    } else {
      wsUrl += `&name=${encodeURIComponent(playerName)}`;
      console.log('Connecting as new player');
    }

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Connected to game server');
        setIsConnected(true);
        setIsReconnecting(false); // Clear reconnecting state on successful connection
        setConnectionStatus('Connected! Joining game...');
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        const message: GameMessage = JSON.parse(event.data);

        // Handle reconnect token message
        if (message.type === 'reconnect_token') {
          const tokenData = message.data as ReconnectTokenData;
          reconnectTokenRef.current = tokenData.reconnectToken;

          // Store in localStorage for page refreshes
          localStorage.setItem(RECONNECT_TOKEN_KEY, tokenData.reconnectToken);
          localStorage.setItem(RECONNECT_TOKEN_GAME_KEY, gameId);

          console.log('Received and stored reconnect token:', tokenData.playerId);
          // Don't pass this to app - it's internal to connection management
          return;
        }

        onMessage(message);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        if (reconnectAttemptsRef.current === 0) {
          setConnectionStatus('Connection error. Attempting to reconnect...');
        }
      };

      ws.onclose = (event) => {
        console.log('Disconnected from game server', event);
        setIsConnected(false);

        // Don't attempt reconnection if this was an intentional disconnect
        if (intentionalDisconnectRef.current) {
          console.log('Intentional disconnect, not attempting to reconnect');
          setIsReconnecting(false);
          return;
        }

        // Only attempt reconnection if we haven't exceeded max attempts
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          setIsReconnecting(true); // Show reconnecting UI
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 10000);

          setConnectionStatus(
            `Connection lost. Reconnecting in ${Math.ceil(delay / 1000)}s... (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setIsReconnecting(false);
          setConnectionStatus('Unable to reconnect to game server. Please refresh the page.');
          onError?.('Unable to reconnect to game server. Please refresh the page.');
        }
      };
    } catch (error) {
      console.error('Failed to connect:', error);
      setConnectionStatus('Failed to connect. Is the game server running?');
      onError?.('Failed to connect to game server');
    }
  }, [gameId, playerName, onMessage, onError]);

  const disconnect = useCallback(() => {
    // Mark this as an intentional disconnect to prevent auto-reconnect
    intentionalDisconnectRef.current = true;

    // Clear reconnect token on intentional disconnect (player is leaving game)
    reconnectTokenRef.current = null;
    localStorage.removeItem(RECONNECT_TOKEN_KEY);
    localStorage.removeItem(RECONNECT_TOKEN_GAME_KEY);

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    setIsReconnecting(false);
  }, []);

  const sendMessage = useCallback((message: AnswerMessage) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    isReconnecting,
    connectionStatus,
    connect,
    disconnect,
    sendMessage,
  };
};